import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { DeductionReleaseDetailType } from "../../types/DeductionRelease";

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#111827",
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 12,
  },
  row: {
    flexDirection: "row",
    marginBottom: 4,
  },
  col: {
    width: "50%",
    paddingRight: 8,
  },
  bold: {
    fontWeight: "bold",
  },
  section: {
    marginTop: 14,
    marginBottom: 8,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#e5e7eb",
    padding: 4,
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    padding: 4,
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
  },
  cellLoc: { width: "50%" },
  cellNum: { width: "25%", textAlign: "right" },
  totals: {
    marginTop: 12,
    padding: 8,
    borderWidth: 1,
    borderColor: "#374151",
  },
  signatureRow: {
    flexDirection: "row",
    marginTop: 28,
  },
  signatureBox: {
    width: "50%",
    paddingRight: 12,
  },
  signatureLine: {
    marginTop: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#111827",
    height: 18,
  },
});

function formatMoney(value: number): string {
  return value.toLocaleString("fi-FI", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fi-FI");
}

interface DeductionReleasePDFProps {
  detail: DeductionReleaseDetailType;
  title?: string;
}

export default function DeductionReleasePDF({
  detail,
  title = "PIDÄTYSTEN PALAUTUS",
}: Readonly<DeductionReleasePDFProps>) {
  const typeLabel =
    detail.objectType === 0
      ? "YLLÄPITO"
      : detail.objectType === 2
        ? "RAT"
        : "RAKENTAMINEN";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>{title}</Text>

        <View style={styles.row}>
          <View style={styles.col}>
            <Text style={styles.bold}>{typeLabel}</Text>
            <Text>
              URAKOITSIJA: {detail.contractorName ?? "-"}
            </Text>
            <Text>URAKKA: {detail.contractName ?? "-"}</Text>
          </View>
          <View style={styles.col}>
            <Text>{detail.contractCode ?? ""}</Text>
            <Text>HELSINKI {formatDate(detail.created)}</Text>
            <Text>
              PUITESOPIMUS: {detail.contractFrameworkNumber ?? "-"}
            </Text>
            <Text>{detail.contractNumber ?? ""}</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text>{detail.description}</Text>
        </View>

        <View style={styles.tableHeader}>
          <Text style={styles.cellLoc}>Kohde</Text>
          <Text style={styles.cellNum}>Laskutettu €</Text>
          <Text style={styles.cellNum}>Pidätetty €</Text>
        </View>
        {detail.objects.map((obj) => (
          <View key={obj.id} style={styles.tableRow}>
            <Text style={styles.cellLoc}>{obj.location ?? obj.id}</Text>
            <Text style={styles.cellNum}>{formatMoney(obj.totalPrice)}</Text>
            <Text style={styles.cellNum}>{formatMoney(obj.holdPrice)}</Text>
          </View>
        ))}

        <View style={styles.totals}>
          <Text>
            Pidätykset yhteensä: {formatMoney(detail.holdTotal)}
          </Text>
          <Text>
            Arvonvähennykset: {formatMoney(Math.abs(detail.valueChange))}
          </Text>
          <Text style={styles.bold}>
            YHTEENSÄ: {formatMoney(detail.netTotal)}
          </Text>
        </View>

        <View style={styles.signatureRow}>
          <View style={styles.signatureBox}>
            <Text style={styles.bold}>
              {detail.foremanName ?? detail.primarySigner ?? ""}
            </Text>
            <View style={styles.signatureLine} />
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.bold}>
              {detail.contractorForemanName ?? detail.secondarySigner ?? ""}
            </Text>
            <View style={styles.signatureLine} />
          </View>
        </View>
      </Page>
    </Document>
  );
}
